import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import PageTabBar from '../components/PageTabBar';
import StockSatelliteHeader from '../features/stock/StockSatelliteHeader';
import { STOCK_PANEL, STOCK_BTN_PRIMARY, STOCK_BTN_SECONDARY, STOCK_BTN_ICON } from '../features/stock/stockV2Ui';
import { STOCK_TABS } from './_stockTabs';
import { History, Store, Warehouse, Printer, Plus, RefreshCw, Package, CheckSquare, Square } from 'lucide-react';

const PERIOD_OPTIONS = [
  { value: 'today', labelKey: 'stockHist.periodToday' },
  { value: 'yesterday', labelKey: 'stockHist.periodYesterday' },
  { value: 'week', labelKey: 'stockHist.periodWeek' },
  { value: 'month', labelKey: 'stockHist.periodMonth' },
];

const StockHistoryPage = () => {
  const { api, user } = useAuth();
  const { t, formatCurrency, formatDate } = useLanguage();
  const navigate = useNavigate();
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('today');
  const [selected, setSelected] = useState({});

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/stock/history?period=${period}`);
      setMovements(r.data || []);
      // Select all by default
      const sel = {};
      (r.data || []).forEach(m => { sel[m.id] = true; });
      setSelected(sel);
    } catch { setMovements([]); }
    setLoading(false);
  }, [api, period]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const toggleSelect = (id) => {
    setSelected(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleAll = () => {
    const allSelected = movements.length > 0 && movements.every(m => selected[m.id]);
    if (allSelected) {
      setSelected({});
    } else {
      const sel = {};
      movements.forEach(m => { sel[m.id] = true; });
      setSelected(sel);
    }
  };

  const selectedMovements = movements.filter(m => selected[m.id]);
  const selectedCount = selectedMovements.length;
  const selectedPieces = selectedMovements.reduce((s, m) => s + (m.quantite || 0), 0);
  const allSelected = movements.length > 0 && movements.every(m => selected[m.id]);

  const goToLabels = () => {
    if (!selectedCount) return;
    const grouped = {};
    selectedMovements.forEach(m => {
      const key = m.article_id || m.reference;
      if (!grouped[key]) {
        grouped[key] = { id: m.article_id, reference: m.reference, designation: m.designation, boutique_qty: 0, depot_qty: 0 };
      }
      if (m.location === 'depot') grouped[key].depot_qty += (m.quantite || 0);
      else grouped[key].boutique_qty += (m.quantite || 0);
    });
    const preselected = Object.values(grouped).map(g => ({
      id: g.id, qty: g.boutique_qty + g.depot_qty,
      boutique_qty: g.boutique_qty, depot_qty: g.depot_qty,
    }));
    navigate('/etiquettes', { state: { preselected, stockFilter: 'all' } });
  };

  const totalPieces = movements.reduce((s, m) => s + (m.quantite || 0), 0);
  const totalBoutique = movements.reduce((s, m) => s + (m.quantite_boutique || 0), 0);
  const totalDepot = movements.reduce((s, m) => s + (m.quantite_depot || 0), 0);

  return (
    <Layout api={api} user={user}>
      <div className="max-w-6xl mx-auto space-y-4 fade-in" data-testid="stock-history-page">
        <PageTabBar tabs={STOCK_TABS} activeColor="rose" />
        <StockSatelliteHeader title={t('stockHist.title')} subtitle={t('stockHist.subtitle')} backTestId="stock-history-back" />
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex rounded-xl border border-white/10 overflow-hidden bg-black/20">
              {PERIOD_OPTIONS.map(p => (
                <button key={p.value} type="button" onClick={() => setPeriod(p.value)}
                  className={`px-3 py-2 min-h-[40px] text-xs font-semibold transition-colors ${period === p.value ? 'bg-red-600 text-white' : 'text-gray-500 hover:bg-white/5 hover:text-gray-300'}`}
                  data-testid={`period-${p.value}`}>
                  {t(p.labelKey)}
                </button>
              ))}
            </div>
            <button type="button" onClick={fetchHistory} className={STOCK_BTN_ICON} title={t('common.refresh')}>
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
          {movements.length > 0 && (
            <button type="button" onClick={goToLabels} disabled={!selectedCount} className={STOCK_BTN_SECONDARY} data-testid="history-print-top-btn">
              <Printer className="w-4 h-4" /> {t('common.print')}
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className={`${STOCK_PANEL} p-3 text-center`}>
            <div className="text-2xl font-bold text-white">{movements.length}</div>
            <div className="text-xs text-gray-500">{t('stockHist.statMovements')}</div>
          </div>
          <div className={`${STOCK_PANEL} p-3 text-center`}>
            <div className="text-2xl font-bold text-red-400">{totalPieces}</div>
            <div className="text-xs text-gray-500">{t('stockHist.statPieces')}</div>
          </div>
          <div className={`${STOCK_PANEL} p-3 text-center`}>
            <div className="flex items-center justify-center gap-1">
              <Store className="w-4 h-4 text-emerald-400" />
              <span className="text-2xl font-bold text-emerald-400">{totalBoutique}</span>
            </div>
            <div className="text-xs text-gray-500">{t('stockHist.locationShop')}</div>
          </div>
          <div className={`${STOCK_PANEL} p-3 text-center`}>
            <div className="flex items-center justify-center gap-1">
              <Warehouse className="w-4 h-4 text-blue-400" />
              <span className="text-2xl font-bold text-blue-400">{totalDepot}</span>
            </div>
            <div className="text-xs text-gray-500">{t('stockHist.locationWarehouse')}</div>
          </div>
        </div>

        <div className={`${STOCK_PANEL} overflow-x-auto`}>
          {loading ? (
            <div className="p-8 text-center text-gray-500">{t('common.loading')}</div>
          ) : movements.length === 0 ? (
            <div className="p-8 text-center">
              <Package className="w-10 h-10 text-gray-700 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">{t('stockHist.empty')}</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 uppercase border-b border-white/10">
                  <th className="px-3 py-3 text-center w-10">
                    <button type="button" onClick={toggleAll} className="hover:text-white transition-colors" data-testid="select-all-btn">
                      {allSelected ? <CheckSquare className="w-4 h-4 text-red-400" /> : <Square className="w-4 h-4" />}
                    </button>
                  </th>
                  <th className="px-3 py-3 text-left">{t('stockHist.colDateTime')}</th>
                  <th className="px-3 py-3 text-left">{t('common.status')}</th>
                  <th className="px-3 py-3 text-left">{t('common.ref')}</th>
                  <th className="px-3 py-3 text-left">{t('stockHist.colDesignation')}</th>
                  <th className="px-3 py-3 text-center">{t('stockHist.colStore')}</th>
                  <th className="px-3 py-3 text-center">{t('stockHist.colLocation')}</th>
                  <th className="px-3 py-3 text-center">{t('stockHist.colQty')}</th>
                  <th className="px-3 py-3 text-right">{t('stockHist.colSalePrice')}</th>
                  <th className="px-3 py-3 text-left">{t('stockHist.colBy')}</th>
                </tr>
              </thead>
              <tbody>
                {movements.map((m, idx) => {
                  const date = m.created_at ? new Date(m.created_at) : null;
                  const isSelected = !!selected[m.id];
                  return (
                    <tr key={m.id || idx}
                      onClick={() => toggleSelect(m.id)}
                      className={`border-b border-white/5 cursor-pointer transition-colors ${isSelected ? 'bg-red-950/20 hover:bg-red-950/30' : 'hover:bg-white/[0.02] opacity-60'}`}
                      data-testid={`history-row-${idx}`}>
                      <td className="px-3 py-2.5 text-center">
                        {isSelected ? <CheckSquare className="w-4 h-4 text-red-400 mx-auto" /> : <Square className="w-4 h-4 text-gray-600 mx-auto" />}
                      </td>
                      <td className="px-3 py-2.5 text-gray-400 text-xs whitespace-nowrap">
                        {date ? formatDate(date, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                      </td>
                      <td className="px-3 py-2.5">
                        {m.action === 'created' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 text-xs font-medium">
                            <Plus className="w-3 h-3" /> {t('stockHist.statusNew')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 text-gray-300 text-xs font-medium">
                            <RefreshCw className="w-3 h-3" /> {t('stockHist.statusRestock')}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-white font-mono text-xs">{m.reference}</td>
                      <td className="px-3 py-2.5 text-white">{m.designation}</td>
                      <td className="px-3 py-2.5 text-center text-xs font-bold text-gray-400">{m.magasin_id || 'M1'}</td>
                      <td className="px-3 py-2.5 text-center">
                        {m.location === 'depot' ? (
                          <span className="inline-flex items-center gap-1 text-xs text-blue-300"><Warehouse className="w-3 h-3" /> {t('stockHist.locationWarehouse')}</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-300"><Store className="w-3 h-3" /> {t('stockHist.locationShop')}</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <span className="inline-block px-2.5 py-0.5 rounded-full bg-red-950/40 text-red-300 font-bold border border-red-500/20">{m.quantite}</span>
                      </td>
                      <td className="px-3 py-2.5 text-right text-white font-semibold">{formatCurrency(m.prix_unitaire || 0)}</td>
                      <td className="px-3 py-2.5 text-gray-500 text-xs">{m.admin_username}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Bottom action bar */}
        {movements.length > 0 && (
          <div className={`${STOCK_PANEL} p-4 flex items-center justify-between flex-wrap gap-3`}>
            <div className="text-sm text-gray-400">
              <span className="text-white font-semibold">{selectedCount}</span> / {movements.length} {selectedCount > 1 ? t('stockHist.selectedMany') : t('stockHist.selectedOne')}
              {selectedCount > 0 && <span className="ml-2 text-red-300">· {selectedPieces} {selectedPieces > 1 ? t('stockHist.labelMany') : t('stockHist.labelOne')}</span>}
            </div>
            <button type="button" onClick={goToLabels} disabled={!selectedCount} className={STOCK_BTN_PRIMARY} data-testid="history-print-labels-btn">
              <Printer className="w-5 h-5" /> {t('stockHist.printCount', { count: selectedPieces })}
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default StockHistoryPage;
